import { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { storageService } from '../../../utils/storage.utils';
import { ErrorHandler } from '../errors/error-handler';

// ─── InterceptorManager ──────────────────────────────────────────────────────

class InterceptorManager {
  // ── Request interceptor ──────────────────────────────────────────────────

  private setupRequestInterceptor(instance: AxiosInstance): void {
    instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const tokens = storageService.getTokens();

        if (tokens?.accessToken) {
          config.headers.Authorization = `Bearer ${tokens.accessToken}`;
        }

        return config;
      },
      (error: AxiosError) => Promise.reject(ErrorHandler.handleError(error))
    );
  }

  // ── Response interceptor ─────────────────────────────────────────────────

  private setupResponseInterceptor(instance: AxiosInstance): void {
    instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        const apiError = ErrorHandler.handleError(error);
        ErrorHandler.logError(apiError);
        return Promise.reject(apiError);
      }
    );
  }

  // ── Public setup entry point ─────────────────────────────────────────────

  setupInterceptors(instance: AxiosInstance): void {
    this.setupRequestInterceptor(instance);
    this.setupResponseInterceptor(instance);
  }
}

// ─── Exports ─────────────────────────────────────────────────────────────────

/**
 * Each Axios instance gets its own InterceptorManager so their refresh-queues
 * don't interfere with each other.
 *
 * TODO: Add token expiry check + Cognito refresh logic once auth is implemented.
 */
export const setupInterceptors = (instance: AxiosInstance): void => {
  new InterceptorManager().setupInterceptors(instance);
};